import { AnyAction, ListenerEffectAPI, ListenerMiddlewareInstance, ThunkDispatch } from '@reduxjs/toolkit'
import { ActionModule } from '@typesApp/ActionModule'
import { ClaimRecord } from '@typesApp/ClaimRecord'
import { UpdateProductArgs } from '@typesApp/CustomerApi'
import { CartBundle } from '@typesApp/Product'
import { claimRecordsApi } from '@redux/queries/claimRecords'
import { unwrapMutationActionResult, unwrapQueryActionResult } from '@redux/queries/unwrap'
import { setClaimRecordUpdateInProgress } from '@redux/slices/connectorSlice'
import { AppState } from '@redux/store'
import { shouldRefreshCartTotals } from '@utils/claimUtil'
import { logger } from '@utils/logger'
import {
  cartProductUpdateController,
  CartProductUpdateService,
  CartProductUpdateParams,
} from './cartProductUpdateController'
import { isChannelUpdateRequired, isProductUpdateRequired } from './claimRecordUpdateUtil'

type DoUpdateParams = {
  isUpdateChannel: boolean
  isUpdateProduct: boolean
  channel: string | undefined
}

type ListenerApi = ListenerEffectAPI<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>
type ListenerInstance = ListenerMiddlewareInstance<unknown, ThunkDispatch<unknown, unknown, AnyAction>, unknown>

/**
 * Update claim record related data if needed, triggered on claim record fetch
 */
export const initClaimRecordUpdateListener = (listenerMiddleware: ListenerInstance) => {
  listenerMiddleware.startListening({
    matcher: claimRecordsApi.endpoints.getClaimRecord.matchFulfilled,

    effect: async (action, listenerApi) => {
      const state = listenerApi.getState() as AppState
      const claimRecord = action.payload
      const isInProgress = state.connector.isClaimRecordUpdateInProgress

      if (!claimRecord || isInProgress) return

      const channel = state.connector?.environment?.channel
      const isUpdateProduct = isProductUpdateRequired(claimRecord.cartStatus) || shouldRefreshCartTotals(claimRecord)
      const isUpdateChannel = channel !== undefined && isChannelUpdateRequired(claimRecord, channel)
      if (!isUpdateProduct && !isUpdateChannel) return

      listenerApi.dispatch(setClaimRecordUpdateInProgress(true))

      try {
        if (await doUpdate(listenerApi, claimRecord, { isUpdateChannel, isUpdateProduct, channel })) {
          await safeFetchClaimRecord(listenerApi, claimRecord.id)
        }
      } finally {
        listenerApi.dispatch(setClaimRecordUpdateInProgress(false))
      }
    },
  })
}

const doUpdate = async (
  listenerApi: ListenerApi,
  claimRecord: ClaimRecord,
  { isUpdateChannel, isUpdateProduct, channel }: DoUpdateParams
): Promise<boolean> => {
  let updateChannelSuccess = false
  let updateProductSuccess = false

  if (isUpdateChannel && channel) {
    updateChannelSuccess = await doUpdateChannel(listenerApi, claimRecord, channel)
  }

  if (isUpdateProduct) {
    updateProductSuccess = await doUpdateProducts(listenerApi, claimRecord)
  }

  return updateChannelSuccess || updateProductSuccess
}

const doUpdateChannel = async (
  listenerApi: ListenerApi,
  claimRecord: ClaimRecord,
  channel: string
): Promise<boolean> => {
  const claimRecordId = claimRecord.id
  try {
    await unwrapMutationActionResult<void>(
      listenerApi.dispatch(claimRecordsApi.endpoints.updateChannel.initiate({ claimRecordId, newChannel: channel }))
    )
    return true
  } catch (error) {
    logger.warn('directbilling - update channel error', error)
    return false
  }
}

const doUpdateProducts = async (listenerApi: ListenerApi, claimRecord: ClaimRecord): Promise<boolean> => {
  try {
    const params = getControllerParams(listenerApi)
    const service = getControllerService(listenerApi)
    const controller = cartProductUpdateController(service, params)

    await controller.performUpdateProducts(claimRecord)
    return true
  } catch (error) {
    logger.warn('directbilling - update products error', error)
    return false
  }
}

const getControllerParams = (listenerApi: ListenerApi): CartProductUpdateParams => {
  const { ecommerceCode, ecommerceTransactionHost, channel } = getEnvironmentParams(listenerApi)
  const customerNumber = getCustomerNumber(listenerApi)

  return {
    customerNumber,
    ecommerceCode,
    ecommerceTransactionHost,
    channel,
  }
}

const getControllerService = (listenerApi: ListenerApi): CartProductUpdateService => {
  const updateProductsMutation = async (args: UpdateProductArgs) => {
    return unwrapMutationActionResult<void>(
      listenerApi.dispatch(claimRecordsApi.endpoints.updateProducts.initiate(args))
    )
  }

  const updateRefreshCartFlagMutation = async (claimRecordId: string) => {
    return unwrapMutationActionResult<void>(
      listenerApi.dispatch(claimRecordsApi.endpoints.updateRefreshCartTotalFlag.initiate(claimRecordId))
    )
  }

  const fetchCartBundle = (): Promise<CartBundle> => getActionModule(listenerApi).getCartItemDetails()

  const refreshCart = (): Promise<void> => getActionModule(listenerApi).refreshCart()

  return {
    updateProductsMutation,
    updateRefreshCartFlagMutation,
    fetchCartBundle,
    refreshCart,
  }
}

const getCustomerNumber = (listenerApi: ListenerApi): string | undefined => {
  const state = listenerApi.getState() as AppState
  return state.connector.customer?.id
}

const getActionModule = (listenerApi: ListenerApi): ActionModule => {
  const state = listenerApi.getState() as AppState
  const actionModule = state.connector.actionModule
  if (!actionModule) {
    throw new Error('Bad configuration, action module missing!')
  }
  return actionModule
}

const getEnvironmentParams = (
  listenerApi: ListenerApi
): { ecommerceCode: string; ecommerceTransactionHost: string | undefined; channel: string | undefined } => {
  const state = listenerApi.getState() as AppState
  const { ecommerceCode, ecommerceTransactionHost, channel } = state.connector?.environment ?? {}
  return {
    ecommerceCode,
    ecommerceTransactionHost,
    channel,
  }
}

const safeFetchClaimRecord = async (listenerApi: ListenerApi, claimRecordId: string): Promise<void> => {
  try {
    await unwrapQueryActionResult<ClaimRecord>(
      listenerApi.dispatch(claimRecordsApi.endpoints.getClaimRecord.initiate(claimRecordId, { forceRefetch: true }))
    )
  } catch (error) {
    logger.warn('directbilling - error fetching claim record', error)
  }
}
